/* 孫權（字仲謀）
 * v0.5.3 重製（水系）：天賦江東之主（通用的 crowd：600 內每名友將傷害 +3/4/5%，見 talents.ts）、Q 碧浪奔襲（兩段式水球，爆開留下水域）、
 * W 長江天塹（橫向水道：中間深水擋住敵人、兩側淺灘可走但算水裡）、E 被動（打水裡的敵人普攻加傷）、R 濡須潮界（開關式、持續擴張的潮汐領域）。
 * 他鋪的水都算「水裡」：甘寧的錦帆會擊飛、元素反應算濕（core/elements.ts SG.inWaterArea）。 */
import { SG, K, gd, spMul, sigOf } from '../../core/kit.ts';
import type { Unit } from '../types.ts';
import { defineHero, hook, S, D, P } from '../define.ts';

const ID = 'sunquan';
const inWater = (w: any, u: Unit) => SG.inWaterArea(w, u);
// 水道：中心 c、沿 tx,ty 方向（世界座標，已含 K）展開 half，厚 thick；點在水道裡嗎
const local = (c: any, x: number, y: number) => {
  const dx = x - c.x, dy = (y - c.y) / K;
  return [dx * c.tx + dy * c.ty, -dx * c.ty + dy * c.tx]; // [沿水道, 橫越水道]
};
const inChannel = (c: any, x: number, y: number) => {
  const [along, across] = local(c, x, y);
  return Math.abs(along) <= c.half && Math.abs(across) <= c.thick / 2;
};
// 這一步從水道的一邊跨到另一邊（深水窄，一步就跨得過去）
const crossed = (c: any, x0: number, y0: number, x1: number, y1: number) => {
  const [a0, c0] = local(c, x0, y0), [a1, c1] = local(c, x1, y1);
  return c0 * c1 < 0 && Math.abs(a0) <= c.half && Math.abs(a1) <= c.half;
};
// 水球爆裂：周圍傷害、暈眩，留下水域（第二段與飛到終點共用）
function burstAt(w: any, u: Unit, x: number, y: number, lvl: number) {
  const sk = u.spec.skills[0].then, X = sk.x, L = (v: any) => (Array.isArray(v) ? v[Math.min(v.length, lvl) - 1] : v), sp = spMul(u);
  u.sqOrb = null;
  w.enemiesNear(u, x, y, X.r).forEach((t: Unit) => {
    if (t.structure) return;
    w.damage(u, t, L(X.dmg) * sp, { magic: true, spell: true, stagger: 0.3 });
    if (t.alive) w.applyCC(u, t, { stun: X.stun });
  });
  w.zone(u, { x, y, r: X.r, t: X.water, tick: 0.5, dmg: 0, ground: true, look: 'water', el: null, name: sk.name });
  w.fx({ k: 'slam', x, y, r: X.r, col: '#5fc4e8' });
}
// 潮汐領域收掉：清掉區域、進冷卻
function tideOff(w: any, u: Unit) {
  const T = u.sqTide;
  if (!T) return;
  T.zone.t = 0; u.sqTide = null;
  if (u.rc) u.rc[3] = null;
  u.cd[3] = w.skillCd(u, u.spec.skills[3], 3, u.skillLv[3] || 1);
}

export default defineHero({
  id: ID, name: '孫權', sub: '字仲謀', faction: 'wu', role: '法師', weapon: 'sword',
  look: { ranged: true, head: 'crown', beard: 'goatee', beardColor: '#7a3f8a', eyeColor: '#2fae9a', robe: true, fx: '#5fc4e8', c: { cloth: '#9a2f2a', sleeve: '#9a2f2a', armor: '#e9c46a', cape: '#6e1f1b' } },
  skills: [
    ['碧浪奔襲', 'bolt', '丟出一枚大水球（半徑 60），穿過的敵人受到 {30/48/65/83} 法術傷害。再按一次或飛到終點時水球爆裂。',
      { x: { k: 'sqOrb', dmg: [30, 48, 65, 83], speed: 335, at: 0.1 }, aim: D(800, 60), cd: [12, 11, 10, 9], mp: [70, 80, 90, 100], recast: 2.6, el: null,
        then: ['水球爆裂', 'thunder', '水球爆裂，周圍 200 內的敵人受到 {40/60/80/100} 法術傷害並暈眩 0.8 秒，留下 3 秒水域。', { x: { k: 'sqBurst', dmg: [40, 60, 80, 100], r: 200, stun: 0.8, water: 3, at: 0.01 }, aim: S(0), mp: 0, free: true, el: null }] }],
    ['長江天塹', 'trap', '在指定地點召喚一道橫向的水道 {4/4.5/5/5.5} 秒（長 140）。中間是深水：敵人走不進去，被擊退、衝刺或閃現進去的敵人移速 -50%；兩側各 40 是淺灘：可以走，但算在水裡。',
      { x: { k: 'sqChannel', dur: [4, 4.5, 5, 5.5], half: 70, thick: 20, bank: 40, slow: 50, at: 0.2 }, aim: P(415, 70, [70, 100]), cd: [20, 19, 18, 17], mp: 90, el: null }],
    ['濤聲', 'passive', '普攻打中站在水裡（含河道）的敵人，額外造成 {20/35/50/65} 法術傷害。', { pv: {}, sig: 'surf', dmg: [20, 35, 50, 65] }],
    ['濡須潮界', 'command', '開關技：在指定地點召喚潮汐領域，從半徑 50 在 3 秒內擴張到 200；領域裡的敵人每秒受到 {40/60/80} 法術傷害、移速 -{30/35/40}%。開啟時每秒耗 {30/40/50} 氣，再按一次關閉；關閉後冷卻 {6/5/4} 秒。',
      { x: { k: 'sqTide', dps: [40, 60, 80], slow: [30, 35, 40], upkeep: [30, 40, 50], r0: 50, r1: 200, grow: 3, at: 0.2 }, aim: P(600, 200), cd: [10, 8.3, 6.7], mp: 100, recast: 9999, free: true, minor: true, el: null }],
  ],
  // 專屬技能（技能資料裡的 x.k）
  kinds: {
    sqOrb(u, a, X) {
      const sp = spMul(u);
      const p = this.shootProj(u, { dir: a.dir, speed: X.speed, range: this.skillRange(a.sk, a.lvl), shape: 'waterball', col: '#5fc4e8', rad: 60,
        filter: (t: Unit) => !t.structure,
        onHit: (t: Unit) => { this.damage(u, t, X.dmg * sp, { magic: true, spell: true, stagger: 0.15 }); return false; }, // 穿過
        onEnd: () => { if (u.sqOrb === p) { const rc = u.rc && u.rc[0]; if (rc && rc.alt) { u.cd[0] = Math.max(0, rc.cd0 - (this.t - rc.at)); u.rc[0] = null; } burstAt(this, u, p.x, p.y, a.lvl); } }, // 飛到終點自己爆
      });
      u.sqOrb = p;
    },
    sqBurst(u, a, X) {
      const p = u.sqOrb;
      if (!p) return;
      this.projs = this.projs.filter((q: any) => q !== p);
      burstAt(this, u, p.x, p.y, a.lvl);
    },
    sqChannel(u, a, X) {
      const dir = a.dir, tx = -dir[1], ty = dir[0]; // 橫向：垂直於施放方向
      const c = { team: u.team, owner: u, x: a.pt[0], y: a.pt[1], tx, ty, half: X.half, thick: X.thick, until: this.t + X.dur, slow: X.slow / 100 };
      (this.sqChannels || (this.sqChannels = [])).push(c);
      // 畫面與「在水裡」：沿水道鋪兩層水域。外層淺灘（兩側各 bank 寬，可以走、算水裡），內層深水（擋路靠上面的 c）
      for (const [look, r] of [['water', X.thick / 2 + X.bank], ['deep', X.thick / 2 + 8]] as const)
        for (let j = 0, n = Math.ceil((X.half * 2) / (r * 0.9)); j <= n; j++) { // 每層照自己的半徑排，圓和圓接得起來
          const s = -X.half + (2 * X.half * j) / n;
          this.zone(u, { x: c.x + tx * s, y: c.y + ty * s * K, r, t: X.dur, tick: 0.5, dmg: 0, ground: true, look, water: true, el: null, name: a.sk.name });
        }
      // 剛好站在水道上的敵人推到靠近自己的那一側
      for (const t of this.units) if (t.alive && t.team !== u.team && !t.structure && t.kind !== 'ward' && inChannel(c, t.x, t.y)) { const s = (t.x - c.x) * -ty + ((t.y - c.y) / K) * tx >= 0 ? 1 : -1, need = X.thick / 2 + 12; t.x += -ty * s * need; t.y += tx * s * need * K; }
    },
    sqTide(u, a, X) {
      if (u.sqTide) { tideOff(this, u); return; } // 再按一次：關閉
      const z: any = { x: a.pt[0], y: a.pt[1], r: X.r0, t: 1e9, tick: 0.5, dmg: X.dps * 0.5 * spMul(u), slow: X.slow / 100, ground: true, water: true, look: 'tide', el: null, name: a.sk.name };
      this.zone(u, z);
      u.sqTide = { zone: this.zones[this.zones.length - 1], t0: this.t, r0: X.r0, r1: X.r1, grow: X.grow, upkeep: X.upkeep };
    },
  },
  // 被動與專屬效果：hook(時間點, 順位, 處理)（時間點一覽見 core/hooks.ts）
  hooks: [
    // 濤聲：普攻打中水裡的敵人額外法術傷害
    hook('onHit', 26, function (u, t) {
      const g = u.spec && u.spec.id === ID ? sigOf(u, 'surf') : null;
      if (!g || !t.alive || t.structure || t.team === u.team || !inWater(this, t)) return;
      this.damage(u, t, g.v('dmg'), { magic: true, quiet: true });
    }),
    // 長江天塹：敵人走不進深水（從外面走進來就退回去）；人在深水裡移速 -50%。兩側淺灘可以走
    hook('step.after', 20, function (dt) {
      this.dt0 = dt;
      const cs = this.sqChannels;
      if (!cs || !cs.length) return;
      this.sqChannels = cs.filter((c: any) => c.until > this.t);
      for (const c of this.sqChannels) for (const t of this.units) {
        if (!t.alive || t.team === c.team || t.structure || t.kind === 'ward' || t.kind === 'cart') continue;
        const inside = inChannel(c, t.x, t.y), prev = t.sqPrev;
        // 走進來的退回去：只看這一步走路的距離（被擊退、衝刺、閃現一步移動得遠，不擋）
        const walked = prev && prev[2] === this.t0Step && Math.hypot(t.x - prev[0], (t.y - prev[1]) / K) <= Math.max(30, (this.speed ? this.speed(t) : 400) * this.dt0 * 1.5);
        if ((inside || (walked && crossed(c, prev[0], prev[1], t.x, t.y))) && walked && !t.air && !inChannel(c, prev[0], prev[1])) { t.x = prev[0]; t.y = prev[1]; continue; }
        if (inside) this.addBuff(t, { id: '長江天塹', t: 0.3, neg: true, ms: -c.slow });
      }
    }),
    // 記下每個單位這一步結束時的位置（下一步判斷是不是「走進」水道）
    hook('step.end', 20, function () {
      if (!this.sqChannels || !this.sqChannels.length) return;
      this.t0Step = this.t;
      for (const t of this.units) if (t.alive) t.sqPrev = [t.x, t.y, this.t];
    }),
    // 濡須潮界：領域擴張、每秒耗氣；氣不夠、陣亡就收掉
    hook('step.hero', 40, function (u, dt) {
      const T = u.sqTide;
      if (!T) return;
      if (!u.alive || u.mp < T.upkeep * dt) { tideOff(this, u); return; }
      u.mp -= T.upkeep * dt;
      T.zone.r = T.r0 + (T.r1 - T.r0) * Math.min(1, (this.t - T.t0) / T.grow);
      // 電腦：領域裡沒有敵將超過 2 秒就關掉
      if (!u.human) {
        const foe = this.heroes(1 - u.team).some((h: Unit) => h.alive && gd(h.x, h.y, T.zone.x, T.zone.y) <= T.zone.r + 100);
        if (foe) T.idle = 0; else if ((T.idle = (T.idle || 0) + dt) > 2) tideOff(this, u);
      }
    }),
    // 碧浪奔襲（電腦）：水球飛到敵將身邊就引爆
    hook('step.hero', 41, function (u) {
      const p = u.sqOrb;
      if (!p || u.human || !this.projs.includes(p) || !(u.rc && u.rc[0])) return;
      if (this.heroes(1 - u.team).some((h: Unit) => h.alive && gd(h.x, h.y, p.x, p.y) < 150)) u.input.skill = { i: 0, x: p.x, y: p.y };
    }),
    hook('revive', 70, function (u) { if (u.spec.id === ID) { u.sqOrb = null; if (u.sqTide) tideOff(this, u); } }),
  ],
  // 電腦什麼時候放這招
  ai: {
    sqOrb: ({ t, d }) => !!t && !t.structure && d < 760,
    sqChannel: ({ hero, d }) => hero && d < 560, // 射程 415：放在自己和敵將之間擋路
    sqTide: ({ u, hero, d, foeHeroes }) => !u.sqTide && hero && d < 600 && foeHeroes(700) >= 1,
  },
});
